import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootStore } from 'redux/store';
import {
	Box,
	Button,
	createStyles,
	Divider,
	FormControlLabel,
	Grid,
	makeStyles,
	Paper,
	Radio,
	RadioGroup,
	Switch,
	TextField,
	Theme,
	Tooltip,
} from '@material-ui/core';
import HelpIcon from '@material-ui/icons/Help';
import { LoaderButton } from 'components/LoaderButton';
import { Team } from 'redux/models/team';
import { editTeam as editTeamThunk } from 'redux/thunks/team';
import { updateSeason as updateSeasonThunk, startSeason as startSeasonThunk } from 'redux/thunks';
import { EditTeam } from 'redux/models/editTeam';
import { resetEditTeamSuccess } from 'redux/actions/team';
import { getTeamNameAvailability } from 'api';
import DeleteIcon from '@material-ui/icons/Delete';
import SaveIcon from '@material-ui/icons/Save';
import './TeamSettings.scss';
import { DeleteTeamModal } from 'components/Modals/DeleteTeamModal';
import { Alert } from '@material-ui/lab';
import { EditSeason } from 'redux/models/editSeason';
import { EndSeasonModal } from 'components/Modals/EndSeasonModal';
import TeamSettingsTour from 'components/Tours/TeamSettingsTour';
import { withRouteInformation } from 'components/withRouteInformation';

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		errorText: {
			color: theme.palette.error.main,
		},
	})
);

interface TeamSettingsProps {
	pathname: string;
}

const TeamSettings: React.FC<TeamSettingsProps> = (props: TeamSettingsProps) => {
	const { pathname } = props;
	const classes = useStyles();
	const userState = useSelector((state: RootStore) => state.user);
	const teamState = useSelector((state: RootStore) => state.team);
	const [selectedTeamCopy, setSelectedTeamCopy] = useState<Team>({
		adminOnlyPosts: false,
		isLocked: false,
		teamId: '',
		favorite: false,
		isAdmin: false,
		isPrivate: false,
		hideLeaderboard: false,
		teamName: '',
		personalNotifications: false,
		allNotifications: false,
		currentSeasonId: '',
		currentSeasonName: '',
		previousSeasonEndDate: '',
		previousSeasonName: '',
	});
	const [seasonToEnd, setSeasonToEnd] = useState({} as EditSeason);
	const [teamNameError, setTeamNameError] = useState('');
	const [seasonNameError, setSeasonNameError] = useState('');
	const [validating, setValidating] = useState(false);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [endSeasonModalOpen, setEndSeasonModalOpen] = useState(false);
	const dispatch = useDispatch();
	const privateTooltipText = 'Private groups will not be openly joinable. New members must be added by a current member to join';
	const lockedTooltipText =
		'When a group is locked members cannot send or receive tokens, but the team continues to be joinable and viewable. If your team is not in a season, the team will be locked until a season is started';
	const hideLeaderboardTooltipText =
		'When Hide Leaderboard is enabled the leaderboard is removed from the screen and cannot be seen by anyone on the team. Data is still captured, so this option can be toggled on and off as needed.';
	const [isTourOpen, setIsTourOpen] = useState(false);

	const onTeamNameChange = (event: any) => {
		setSelectedTeamCopy({ ...selectedTeamCopy, teamName: event.target.value } as Team);
	};

	const onIsPrivateChange = () => {
		setSelectedTeamCopy({ ...selectedTeamCopy, isPrivate: !selectedTeamCopy?.isPrivate } as Team);
	};

	const onIsLockedChange = () => {
		setSelectedTeamCopy({ ...selectedTeamCopy, isLocked: !selectedTeamCopy?.isLocked } as Team);
	};

	const onHideLeaderboardChange = () => {
		setSelectedTeamCopy({ ...selectedTeamCopy, hideLeaderboard: !selectedTeamCopy?.hideLeaderboard } as Team);
	};

	const onAdminsCanSendChange = () => {
		setSelectedTeamCopy({ ...selectedTeamCopy, adminOnlyPosts: !selectedTeamCopy?.adminOnlyPosts } as Team);
	};

	const onUpdateSeasonName = (event: any) => {
		setSelectedTeamCopy({ ...selectedTeamCopy, currentSeasonName: event.target.value } as Team);
	};

	const onUpdateSeason = () => {
		if (!selectedTeamCopy.currentSeasonName) {
			setSeasonNameError('You must provide a valid season name');
			return;
		}

		const season: EditSeason = {
			teamId: selectedTeamCopy.teamId,
			seasonId: selectedTeamCopy.currentSeasonId,
			seasonName: selectedTeamCopy.currentSeasonName,
		};

		dispatch(updateSeasonThunk(season));
		setSeasonNameError('');
	};

	const onEndSeason = () => {
		if (!selectedTeamCopy.currentSeasonName) {
			setSeasonNameError('You must provide a valid season name');
			return;
		}

		const season: EditSeason = {
			teamId: selectedTeamCopy.teamId,
			seasonId: selectedTeamCopy.currentSeasonId,
			seasonName: selectedTeamCopy.currentSeasonName,
		};
		setSeasonToEnd(season);
		setEndSeasonModalOpen(true);
		setSeasonNameError('');
	};

	const onStartSeason = () => {
		dispatch(startSeasonThunk(selectedTeamCopy.teamId));
	};

	const saveChanges = async (event: any) => {
		event.preventDefault();
		try {
			setValidating(true);
			if (JSON.stringify(userState.selectedTeam) === JSON.stringify(selectedTeamCopy)) {
				// onCloseClicked();
			} else {
				if (selectedTeamCopy) {
					if (selectedTeamCopy?.teamName?.length < 1) throw Error('Please provide a team name.');
					// Check if team name is available
					let isAvailable = await getTeamNameAvailability(selectedTeamCopy.teamName);
					if (!isAvailable && selectedTeamCopy.teamName === userState.selectedTeam?.teamName) isAvailable = true;
					if (!isAvailable) throw Error('Team name is not available');

					const team: EditTeam = {
						teamId: selectedTeamCopy?.teamId,
						teamName: selectedTeamCopy?.teamName,
						isPrivate: selectedTeamCopy?.isPrivate,
						isLocked: selectedTeamCopy?.isLocked,
						hideLeaderboard: selectedTeamCopy?.hideLeaderboard,
						adminOnlyPosts: selectedTeamCopy?.adminOnlyPosts,
					};
					dispatch(editTeamThunk(team));
				}
			}
		} catch (error) {
			setTeamNameError(error.message || 'Invalid Input');
		} finally {
			setValidating(false);
		}
	};

	useEffect(() => {
		if (userState.selectedTeam) {
			setSelectedTeamCopy(userState.selectedTeam);
		}
	}, [userState.selectedTeam]);

	useEffect(() => {
		if (teamState.editingTeamSuccess) {
			dispatch(resetEditTeamSuccess());
		}
	}, [teamState.editingTeamSuccess, dispatch]);

	// Use effect for managing if the tour should show or not
	useEffect(() => {
		if (userState.userId && pathname.includes('/details/settings') && !userState.tours.includes('TeamSettingsTour')) {
			setTimeout(function () {
				setIsTourOpen(true);
			}, 700);
		}
	}, [userState.tours, userState.userId, pathname]);

	return (
		<>
			<Grid container direction='column' alignContent='center' alignItems='center'>
				{userState?.selectedTeam?.isAdmin && (
					<Alert severity='info' className='settings-admin-info'>
						As an 'Administrator' of this team you can modify all team settings and delete the team if needed. Others are limited to simply viewing
						these settings.
					</Alert>
				)}
				<form onSubmit={saveChanges} className='team-details__form'>
					{userState?.selectedTeam?.isAdmin && (
						<>
							<Grid container item>
								<Divider light className='team-details__divider' />
							</Grid>
							<Grid container item justify='space-between'>
								<Button
									variant='outlined'
									className='team-details__delete-button'
									onClick={() => setDeleteModalOpen(true)}
									startIcon={<DeleteIcon />}
								>
									Delete Team
								</Button>
							</Grid>
						</>
					)}
					<Paper elevation={2}>
						<Box display='flex' flexWrap='wrap' flexDirection='column' className='team-details__container'>
							<Grid container item xs={12} alignItems='flex-start' alignContent='flex-start' className='team-details'>
								<Grid alignItems='flex-start' className='team-details__team-name' container item>
									<TextField
										id='teamName'
										fullWidth
										label='Team Name'
										variant='outlined'
										value={selectedTeamCopy?.teamName}
										onChange={onTeamNameChange}
										error={teamNameError?.length > 0 ? true : false}
										disabled={!userState?.selectedTeam?.isAdmin}
									/>
									<div className={`team-details__subtext ${teamNameError ? classes.errorText : null}`}>{teamNameError || 'Required'}</div>
								</Grid>
								<Grid container item>
									<Divider light className='team-details__divider' />
								</Grid>
								<Grid container item alignContent='center'>
									<Grid container item id='permissionTable'>
										<Grid item>
											<div className='team-details__subheader'>Token Permission</div>
											<RadioGroup>
												<FormControlLabel
													value='All'
													control={
														<Radio
															name='token-permissions'
															onChange={onAdminsCanSendChange}
															checked={selectedTeamCopy?.adminOnlyPosts === false}
															color='primary'
															disabled={!userState?.selectedTeam?.isAdmin}
														/>
													}
													label='All members can send tokens'
												/>
												<FormControlLabel
													value='Admin'
													control={
														<Radio
															name='token-permissions'
															onChange={onAdminsCanSendChange}
															checked={selectedTeamCopy?.adminOnlyPosts === true}
															color='primary'
															disabled={!userState?.selectedTeam?.isAdmin}
														/>
													}
													label='Only admins can send tokens'
												/>
											</RadioGroup>
										</Grid>
									</Grid>
								</Grid>
								<Grid container item>
									<Divider light className='team-details__divider' />
								</Grid>
								<Grid item container id='teamSettings'>
									<div className='team-details__subheader'>Settings</div>
									<Grid container item alignItems='center'>
										<FormControlLabel
											checked={selectedTeamCopy.isPrivate}
											control={
												<Switch
													name='checkedB'
													color='primary'
													classes={{ track: !selectedTeamCopy?.isPrivate && true ? 'switch-disabled-unchecked' : '' }}
												/>
											}
											id='privateSwitch'
											label='Private'
											className='team-details__switch-label'
											onChange={onIsPrivateChange}
											disabled={!userState?.selectedTeam?.isAdmin}
										/>
										<Tooltip title={privateTooltipText}>
											<HelpIcon color='primary' fontSize='small' />
										</Tooltip>
									</Grid>

									<Grid container item alignItems='center'>
										<FormControlLabel
											checked={selectedTeamCopy?.isLocked}
											control={
												<Switch
													name='checkedC'
													color='primary'
													classes={{ track: !selectedTeamCopy?.isLocked && true ? 'switch-disabled-unchecked' : '' }}
												/>
											}
											id='lockedSwitch'
											label='Locked'
											className='team-details__switch-label'
											onChange={onIsLockedChange}
											disabled={!userState?.selectedTeam?.isAdmin || !selectedTeamCopy?.currentSeasonId}
										/>
										<Tooltip title={lockedTooltipText}>
											<HelpIcon color='primary' fontSize='small' />
										</Tooltip>
									</Grid>

									<Grid container item alignItems='center'>
										<FormControlLabel
											checked={selectedTeamCopy?.hideLeaderboard}
											control={
												<Switch
													name='checkedD'
													color='primary'
													classes={{ track: !selectedTeamCopy?.hideLeaderboard && true ? 'switch-disabled-unchecked' : '' }}
												/>
											}
											id='leaderboardSwitch'
											label='Hide Leaderboard'
											className='team-details__switch-label'
											onChange={onHideLeaderboardChange}
											disabled={!userState?.selectedTeam?.isAdmin}
										/>
										<Tooltip title={hideLeaderboardTooltipText}>
											<HelpIcon color='primary' fontSize='small' />
										</Tooltip>
									</Grid>
								</Grid>
								{userState?.selectedTeam?.isAdmin && (
									<>
										<Grid container item>
											<Divider light className='team-details__divider' />
										</Grid>
										<LoaderButton type='submit' loading={teamState.editingTeam || validating} startIcon={<SaveIcon />}>
											Save Changes
										</LoaderButton>
									</>
								)}
							</Grid>
						</Box>
					</Paper>
					<Paper elevation={2} className='team-details__seasons'>
						<Box display='flex' flexWrap='wrap' flexDirection='column' className='team-details__container'>
							<Grid container item xs={12} alignItems='flex-start' alignContent='flex-start' className='team-details' direction='column'>
								{selectedTeamCopy.currentSeasonId && (
									<>
										<Grid alignItems='flex-start' className='team-details__seasons-form' container item>
											<div className='team-details__seasons-title'>Season</div>
											<Grid item container alignItems='center'>
												<Grid item xs={10} style={{ paddingRight: '1rem' }}>
													<TextField
														fullWidth
														label='Season Name'
														variant='outlined'
														id='seasonName'
														value={selectedTeamCopy?.currentSeasonName}
														error={seasonNameError?.length > 0 ? true : false}
														disabled={!userState?.selectedTeam?.isAdmin}
														onChange={onUpdateSeasonName}
													/>
												</Grid>
											</Grid>
										</Grid>
										<div className={`team-details__subtext ${seasonNameError ? classes.errorText : null}`}>
											{seasonNameError || 'Required'}
										</div>
									</>
								)}
								<Grid container item>
									{selectedTeamCopy.previousSeasonEndDate && selectedTeamCopy.previousSeasonName ? (
										<div
											className={`${
												selectedTeamCopy.currentSeasonId ? 'team-details__previous-season' : 'team-details__previous-season--no-margin'
											}`}
										>
											Previous season <strong>"{selectedTeamCopy.previousSeasonName}"</strong> ended on{' '}
											{new Date(selectedTeamCopy.previousSeasonEndDate).toLocaleDateString()}
										</div>
									) : (
										<div
											className={`${
												selectedTeamCopy.currentSeasonId ? 'team-details__previous-season' : 'team-details__previous-season--no-margin'
											}`}
										>
											No previous seasons
										</div>
									)}
								</Grid>
								{userState?.selectedTeam?.isAdmin && (
									<>
										<Grid container item>
											<Divider light className='team-details__divider' />
										</Grid>
										<Grid direction='row' container>
											{selectedTeamCopy.currentSeasonId ? (
												<>
													<LoaderButton color='none' type='button' id='endSeasonButton' loading={false} onHandleClick={onEndSeason}>
														End Season
													</LoaderButton>
													<LoaderButton
														type='button'
														loading={teamState.updatingSeason}
														style={{ marginLeft: '1rem' }}
														onHandleClick={onUpdateSeason}
														startIcon={<SaveIcon />}
													>
														Save Changes
													</LoaderButton>
												</>
											) : (
												<LoaderButton type='button' loading={teamState.startingSeason} onHandleClick={onStartSeason}>
													Start Season
												</LoaderButton>
											)}
										</Grid>
									</>
								)}
							</Grid>
						</Box>
					</Paper>
				</form>

				<DeleteTeamModal
					teamName={selectedTeamCopy.teamName}
					teamId={selectedTeamCopy.teamId}
					open={deleteModalOpen}
					allowClickOutsideToClose={true}
					onCloseClicked={() => setDeleteModalOpen(false)}
				/>
				<EndSeasonModal
					season={seasonToEnd}
					open={endSeasonModalOpen}
					allowClickOutsideToClose={true}
					onCloseClicked={() => setEndSeasonModalOpen(false)}
				/>
			</Grid>
			<TeamSettingsTour open={isTourOpen} onClose={() => setIsTourOpen(false)} />
		</>
	);
};

export default withRouteInformation(TeamSettings);
